import type { FunctionComponent, RefObject } from 'preact';

import {
  createSelectRepeater,
  type Params as SelectParams,
  SelectContextProvider,
} from 'Components/smallComponents/select';
import type { RepeaterFunctionInvoker, TemplateFunction } from 'Components/types';
import { replaceVerifyFitmentVehicleValue } from 'Core/actions/fitmentSearch/request';
import { vehicleSelected } from 'Core/actions/fitmentSearch/vehicle';
import { useDispatch } from 'Core/store';
import { type FacetCollection, Vehicle } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import type { ServerModel } from 'Modules/serverApi/types';
import { cloneSafe, type CloneSafeProps } from 'Utils/components';
import safeTemplateCall from 'Utils/safeTemplateCall';

import { type Params as UseCollapsingParams, useCollapsing } from '../vehicleWidget/useCollapsing';
import type { LayoutType } from '../vehicleWidget/useLayoutState';

export type Params = {
  name: string;
  template: string;
  selects: RepeaterFunctionInvoker<SelectParams>;
  fitmentSearchTitle: string;
  aboutToChange: boolean;
  allowToDiscardVehicle: boolean;
  allowToSetVehicle: boolean;
  vehicleString: string;
  productData: ServerModel.SearchItem;
  discardVehicle: () => void;
  openDialog: () => void;
  selectVehicle: () => void;
} & UseCollapsingParams;

type Props = {
  template: TemplateFunction<Params>;
  rootRef: RefObject<HTMLElement>;
  appendedClasses: CloneSafeProps['appendedClasses'];
  name: string;
  layoutState: LayoutType;
  facets: FacetCollection;
  fitmentFields: string[];
  responseVehicle: Vehicle;
  aboutToChange: boolean;
  allowToDiscardVehicle: boolean;
  initCollapsed: boolean;
  isAllFieldsSelected: boolean;
  isAutoVehicleSelectionDisabled?: boolean;
  isVehicleSelected: boolean;
  useNativeDropdown?: boolean;
  useSearchableDropdown?: boolean;
  verifyFitmentProductData: ServerModel.SearchItem;
  discardVehicle: () => void;
  onSelectVehicle: () => void;
  openDialog: () => void;
};

const VerifyFitmentDropdowns: FunctionComponent<Props> = ({
  template,
  rootRef,
  appendedClasses,
  name: widgetName,
  layoutState,
  facets,
  fitmentFields,
  responseVehicle,
  aboutToChange,
  allowToDiscardVehicle,
  initCollapsed,
  isAllFieldsSelected,
  isAutoVehicleSelectionDisabled,
  isVehicleSelected,
  useNativeDropdown,
  useSearchableDropdown,
  verifyFitmentProductData,
  discardVehicle: discardVehicleProp,
  onSelectVehicle,
  openDialog,
}) => {
  const dispatch = useDispatch();

  const collapsing = useCollapsing(rootRef, isVehicleSelected, layoutState, initCollapsed, aboutToChange);

  if (!facets.length) {
    return;
  }

  const selectVehicle = () => {
    if (isAllFieldsSelected) {
      dispatch(
        vehicleSelected(responseVehicle, { isSelectedFromVerifyFitment: true, sendIsolatedRequest: true }),
      );

      fitmentSearchConfig.onVehicleSelected(responseVehicle.filteredFieldMap);
      onSelectVehicle();

      if (!collapsing.collapsed) {
        collapsing.toggleCollapsed();
      }
    }
  };
  const discardVehicle = () => {
    discardVehicleProp();

    if (isAllFieldsSelected) {
      fitmentSearchConfig.onVehicleDiscarded();
    }
  };

  const selects = createSelectRepeater(
    facets.map(({ field, name, facetedValues }, index) => ({
      entries: facetedValues.map((v) => ({
        value: v.value,
        term: v.term,
        payload: v.payload,
        selected: v.isSelected,
      })),
      extraClassName: 'cm_vehicle-widget_select',
      disabled: !facetedValues.length,
      hideNullOption: true,
      field,
      title: name,
      key: field,
      index,
      useNativeDropdown: !useSearchableDropdown && useNativeDropdown,
      isSearchable: useSearchableDropdown,
      onChange: (term) => {
        const facetValue = facetedValues.find((v) => v.term === term);
        if (facetValue) {
          dispatch(
            replaceVerifyFitmentVehicleValue(facetValue, {
              isAutoVehicleSelectionDisabled:
                facets.hasSelectionAfter(field) || isAutoVehicleSelectionDisabled,
              fitmentFields,
            }),
          );
        }
      },
    })),
  );

  const component = safeTemplateCall(
    template,
    {
      name: widgetName,
      selects,
      vehicleString: responseVehicle.toString(),
      allowToSetVehicle: isAllFieldsSelected,
      allowToDiscardVehicle,
      aboutToChange,
      discardVehicle,
      selectVehicle,
      openDialog,
      fitmentSearchTitle: fitmentSearchConfig.fitmentSearchTitle,
      productData: verifyFitmentProductData,
      template: 'active',
      ...collapsing,
    },
    'VerifyFitmentDropdowns',
  );

  return <SelectContextProvider>{cloneSafe(component, rootRef, { appendedClasses })}</SelectContextProvider>;
};

export default VerifyFitmentDropdowns;
